import React, { useContext } from 'react';
import styled from 'styled-components';
import { usePostHog } from 'posthog-js/react';
import { ThemeContext } from '../ThemeContext';

const Container = styled.div`
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h3`
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme === 'light' ? '#333' : '#eee'};
`;

const ControlGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ColorButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid ${props => props.active ? (props.theme === 'light' ? '#333' : '#fff') : 'rgba(0,0,0,0.1)'};
  background-color: ${props => props.color} !important; /* Force override */
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    transform: scale(1.1);
    border-color: ${props => props.theme === 'light' ? '#666' : '#ccc'};
  }
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 2px solid ${props => props.active ? 'var(--accent-color)' : (props.theme === 'light' ? '#ccc' : '#444')};
  background-color: ${props => props.active 
    ? (props.theme === 'light' ? '#333' : '#eee') 
    : (props.theme === 'light' ? '#fff' : '#222')};
  color: ${props => props.active 
    ? (props.theme === 'light' ? '#fff' : '#222') 
    : (props.theme === 'light' ? '#333' : '#eee')};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 2px 8px rgba(0,0,0,0.2)' : 'none'};

  &:hover {
    border-color: var(--accent-color);
    background-color: ${props => props.active ? (props.theme === 'light' ? '#000' : '#fff') : (props.theme === 'light' ? '#f0f0f0' : '#333')};
  }
`;

const Label = styled.div`
  font-size: 12px;
  color: ${props => props.theme === 'light' ? '#666' : '#aaa'};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: bold;
`;

const SettingsSection = () => {
  const { 
    theme, 
    setTheme, 
    accentColor, 
    setAccentColor,
    backgroundType,
    setBackgroundType,
    backgroundSolidColor,
    setBackgroundSolidColor
  } = useContext(ThemeContext);
  const posthog = usePostHog();

  const accentColors = [
    '#ff8c00', // Orange
    '#a393f5', // Purple
    '#4cc9f0', // Blue
    '#f72585', // Pink
    '#4caf50', // Green
    '#e91e63'  // Crimson
  ];

  const bgColors = [
    { name: 'Default', light: '#f5f5f5', dark: '#121212' },
    { name: 'Midnight', light: '#2c3e50', dark: '#0f172a' },
    { name: 'Forest', light: '#2d5a27', dark: '#064e3b' },
    { name: 'Berry', light: '#702459', dark: '#4c1d95' },
    { name: 'Slate', light: '#64748b', dark: '#1e293b' }
  ];

  return (
    <Container>
      <Section>
        <Title theme={theme}>Appearance</Title>
        <Label theme={theme}>Theme</Label>
        <ControlGroup>
          <Button
            theme={theme}
            active={theme === 'light'}
            onClick={() => { posthog?.capture('setting_changed', { setting: 'theme', value: 'light' }); setTheme('light'); }}
          >
            Light
          </Button>
          <Button
            theme={theme}
            active={theme === 'dark'}
            onClick={() => { posthog?.capture('setting_changed', { setting: 'theme', value: 'dark' }); setTheme('dark'); }}
          >
            Dark
          </Button>
        </ControlGroup>
      </Section>

      <Section>
        <Label theme={theme}>Accent Color</Label>
        <ControlGroup>
          {accentColors.map(color => (
            <ColorButton
              key={color}
              color={color}
              active={accentColor === color}
              theme={theme}
              onClick={() => { posthog?.capture('setting_changed', { setting: 'accent_color', value: color }); setAccentColor(color); }}
              title={color}
            />
          ))}
        </ControlGroup>
      </Section>

      <Section>
        <Title theme={theme}>Desktop</Title>
        <Label theme={theme}>Background Style</Label>
        <ControlGroup>
          <Button
            theme={theme}
            active={backgroundType === 'wave'}
            onClick={() => { posthog?.capture('setting_changed', { setting: 'background_type', value: 'wave' }); setBackgroundType('wave'); }}
          >
            Animated Waves
          </Button>
          <Button
            theme={theme}
            active={backgroundType === 'solid'}
            onClick={() => { posthog?.capture('setting_changed', { setting: 'background_type', value: 'solid' }); setBackgroundType('solid'); }}
          >
            Solid Color
          </Button>
        </ControlGroup>
      </Section>

      {backgroundType === 'solid' && (
        <Section>
          <Label theme={theme}>Solid Background Color</Label>
          <ControlGroup>
            {bgColors.map(bg => {
              const color = theme === 'light' ? bg.light : bg.dark;
              return (
                <ColorButton
                  key={bg.name}
                  color={color}
                  active={backgroundSolidColor === color}
                  theme={theme}
                  onClick={() => { posthog?.capture('setting_changed', { setting: 'background_color', value: bg.name }); setBackgroundSolidColor(color); }}
                  title={bg.name}
                />
              );
            })}
          </ControlGroup>
        </Section>
      )}
    </Container>
  );
};

export default SettingsSection;